import PropTypes from 'prop-types';
import React, { useState } from 'react';
import GenericGridComponent from '../../GenericGridComponent/GenericGridComponent';
import { formatUnixTimestampToHumanReadableFormat } from '../../utils/DateUtils';
import ParticipantNameButton from './ParticipantNameButton';
import StatusBadge from './StatusBadge';
import './TrackingTable.scss';

const TrackingTable = ({
  data,
  selectedCompanyIds,
  onRowSelection,
  onRowClick,
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const participantNameFormatter = ({ row }) => (
    <ParticipantNameButton
      participantName={row.participantName}
    />
  );

  const statusBadgeFormatter = ({ row }) => <StatusBadge status={row.processingStatus} />;
  const sensitiveFormatter = ({ row }) => (row.sensitive ? "Yes" : "No");
  const submissionDateFormatter = ({ row }) => formatUnixTimestampToHumanReadableFormat(row.submissionDate);
  const isResubmissionFormatter = ({ row }) => (row.isResubmission ? "Yes" : "No");

  const columns = [{
    key: "participantName",
    name: "Participant Name",
    sortable: true,
    filterable: true,
    width: "25%",
    formatter: participantNameFormatter
  }, {
    key: "assignedTo",
    name: "Assigned To",
    sortable: true,
    filterable: true,
    width: "20%",
    formatter: ({ row }) => row.assignedTo || 'Not Assigned'
  }, {
    key: "processingStatus",
    name: "Processing Status",
    sortable: true,
    filterable: true,
    width: "15%",
    formatter: statusBadgeFormatter
  }, {
    key: "sensitive",
    name: "Sensitive",
    sortable: true,
    filterable: true,
    width: "10%",
    formatter: sensitiveFormatter
  },
  {
    key: "submissionDate",
    name: "Submission Date",
    sortable: true,
    filterable: true,
    formatter: submissionDateFormatter,
    width: "15%"
  },
  {
    key: "isResubmission",
    name: "Re-submission",
    sortable: true,
    filterable: true,
    width: "13%",
    formatter: isResubmissionFormatter
  }];

  const handleRowClick = (row, column) => {
    if (column.key === 'participantName') {
      if (row.surveyYear && row.companyId) {
        onRowClick(row, column);
      } else {
        console.error('Missing required data for navigation:', row);
      }
    }
  };

  // Custom export method to export all data
  const handleCustomExport = async () => {
    try {
      const XLSX = await import("xlsx");
      
      // Create headers
      const headers = [
        "Participant Name",
        "Assigned To", 
        "Processing Status",
        "Sensitive",
        "Submission Date",
        "Re-submission"
      ];

      // Transform data for export (all rows, not just current page)
      const exportData = data.map(row => [
        row.participantName || '',
        row.assignedTo || 'Not Assigned',
        row.processingStatus || '',
        row.sensitive ? "Yes" : "No",
        row.submissionDate ? formatUnixTimestampToHumanReadableFormat(row.submissionDate) : '',
        row.isResubmission ? "Yes" : "No"
      ]);

      // Combine headers and data
      const worksheetData = [headers, ...exportData];

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 25 }, // Participant Name
        { wch: 20 }, // Assigned To
        { wch: 15 }, // Processing Status
        { wch: 10 }, // Sensitive
        { wch: 20 }, // Submission Date
        { wch: 12 }  // Re-submission
      ];
      ws['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Questionnaire Tracking Data');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const fileName = `Questionnaire_Tracking_Data_${timestamp}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  return (
    <section className="tracking-table" aria-label="Survey tracking data">
      <GenericGridComponent
        data={data}
        columns={columns}
        selectedRows={selectedCompanyIds}
        onSelectedRowsChange={onRowSelection}
        handleRowClick={handleRowClick}
        onCurrentPageSelect={setCurrentPage}
        currentPage={currentPage}
        rowKeyGetter={(row) => row.companyId}
        enableRowSelection={true}
        enableGridToCSV={true}
        useCustomExportMethod={true}
        customExportMethod={handleCustomExport}
        fileName="Questionnaire_Tracking_Data"
        itemsName="Questionnaire Tracking Records"
      />
    </section>
  );
};

TrackingTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string.isRequired,
    participantName: PropTypes.string.isRequired,
    assignedTo: PropTypes.string,
    processingStatus: PropTypes.string.isRequired,
    sensitive: PropTypes.bool.isRequired,
    surveyYear: PropTypes.string.isRequired
  })).isRequired,
  selectedCompanyIds: PropTypes.instanceOf(Set).isRequired,
  onRowSelection: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired
};

export default TrackingTable; 